import { Trans } from '@lingui/react/macro';
import {
    LandingPageContacts,
    LandingPageContainer,
    LandingPageDetails,
    LandingPageFloorPlan,
    LandingPageMessage,
    LandingPageMessageList,
    LandingPageSectionType,
    LandingPageSettingDto,
    sortLandingPageMessages,
    useMaintainScrollPosition,
} from '@ygtechnologies/bygr-frontend-component-library';
import { MilestoneTimeline, Tabs } from '@ygtechnologies/rubus-ui';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { getBuyerLandingPage } from '@/services/buyer-dashboard';
import { IState } from '@/state/IState';
import { useAsyncState } from '@/utils/hooks';
import BuyerPageTitle from '../BuyerPageTitle/BuyerPageTitle';
import theme from './LandingPage.module.scss';

interface TabConfig {
    id: string;
    sectionType: LandingPageSectionType;
    route: string;
    name: JSX.Element;
    component: (data: any) => JSX.Element | null;
    shouldHide: boolean;
}

const LandingPage: React.FC = () => {
    const history = useHistory();
    const permissions = useSelector((state: IState) => state.buyer.permissions);
    const [data] = useAsyncState(() => getBuyerLandingPage(permissions!.residentialUnitId), [], null);
    const { tab } = useParams<{ tab: string }>();
    useMaintainScrollPosition();

    const sortedLandingPageMessages = useMemo(() => (!data ? [] : sortLandingPageMessages(data.messages)), [data?.messages]);

    // Define tab configurations
    const tabConfigs: TabConfig[] = useMemo(
        () => [
            {
                id: 'news',
                sectionType: LandingPageSectionType.News,
                route: '/landing/news',
                name: <Trans>News</Trans>,
                shouldHide: false,
                component: () => (
                    <>
                        {data!.milestones.length > 0 && (
                            <div className={theme.milestones}>
                                <MilestoneTimeline>
                                    {data!.milestones.map((m) => (
                                        <MilestoneTimeline.Step key={m.id} {...m} />
                                    ))}
                                </MilestoneTimeline>
                            </div>
                        )}
                        <LandingPageMessageList>
                            {sortedLandingPageMessages.map((m) => (
                                <LandingPageMessage key={m.guid} message={m} />
                            ))}
                        </LandingPageMessageList>
                    </>
                ),
            },
            {
                id: 'details',
                sectionType: LandingPageSectionType.Details,
                route: '/landing/details',
                name: <Trans>Unit details</Trans>,
                shouldHide: !data?.details?.length,
                component: () => <LandingPageDetails details={data!.details} />,
            },
            {
                id: 'floor-plan',
                sectionType: LandingPageSectionType.FloorPlans,
                route: '/landing/floor-plan',
                name: <Trans>Floor plan</Trans>,
                shouldHide: !data?.floorPlans?.length,
                component: () => <LandingPageFloorPlan floorPlans={data!.floorPlans} />,
            },
            {
                id: 'contacts',
                sectionType: LandingPageSectionType.Contacts,
                route: '/landing/contacts',
                name: <Trans>Contacts</Trans>,
                shouldHide: !data?.contacts?.length,
                component: () => <LandingPageContacts contacts={data!.contacts} />,
            },
        ],
        [data, sortedLandingPageMessages]
    );
    // Filter and sort visible tabs based on settings
    const visibleTabs = useMemo(() => {
        if (!data || !data.settings) return tabConfigs;

        const settingsMap = new Map<string, LandingPageSettingDto>();
        data.settings.forEach((setting) => {
            settingsMap.set(setting.sectionType, setting);
        });

        return tabConfigs
            .filter((tabConfig) => {
                const setting = settingsMap.get(tabConfig.sectionType);
                return !setting || setting.isVisible !== false; // Default to visible if setting doesn't exist
            })
            .filter((tabConfig) => !tabConfig.shouldHide)
            .sort((a, b) => {
                const settingA = settingsMap.get(a.sectionType);
                const settingB = settingsMap.get(b.sectionType);
                const orderA = settingA ? settingA.sortOrder : 999;
                const orderB = settingB ? settingB.sortOrder : 999;
                return orderA - orderB;
            });
    }, [data?.settings, tabConfigs]);
    // Determine active tab
    const activeTabId = useMemo(() => {
        if (tab && visibleTabs.some((t) => t.id === tab)) {
            return tab;
        }
        // Default to first visible tab if current tab is not visible
        return visibleTabs.length > 0 ? visibleTabs[0].id : '';
    }, [tab, visibleTabs]);
    // Redirect if current tab is not visible
    useEffect(() => {
        if (visibleTabs.length > 0 && tab && !visibleTabs.some((t) => t.id === tab)) {
            history.replace(`/landing/${visibleTabs[0].id}`);
        }
    }, [tab, visibleTabs, history]);

    if (!data) return null;



    return (
        <div className={theme.landingPage}>
            <BuyerPageTitle leftPadding />

            <LandingPageContainer data={data}>
                {visibleTabs.length > 0 ? (
                    <div className={theme.tabsWrapper}>
                        <Tabs activeId={activeTabId} variant="pill" className={theme.tabContainer}>
                            {visibleTabs.map((tabConfig) => (
                                <Tabs.Tab
                                    key={tabConfig.id}
                                    id={tabConfig.id}
                                    as={Link}
                                    to={tabConfig.route}
                                    label={tabConfig.name}
                                />
                            ))}
                        </Tabs>
                        {visibleTabs.find((t) => t.id === activeTabId)?.component(data)}
                    </div>
                ) : (
                    <div className={theme.noTabs}>
                        <Trans>More information will be available soon</Trans>
                    </div>
                )}
            </LandingPageContainer>
        </div>
    );
};

export default LandingPage;
